import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Gauge, LayoutGrid, ShieldCheck, Timer } from 'lucide-react';
import { SNAPCHAT_BID_STRATEGIES, SNAPCHAT_BRAND_SAFETY, SNAPCHAT_PACING, SNAPCHAT_POSITIONS, defaultSnapDelivery } from './snapchatConfig';

const selectCls = 'bg-white/5 border-white/10 text-white';
const Block = ({ icon: Icon, title, children, testId }) => (
  <div className="space-y-2 p-3 rounded-xl bg-white/5 border border-white/10" data-testid={testId}>
    <Label className="text-white/90 text-sm font-medium flex items-center gap-2"><Icon className="h-4 w-4" /> {title}</Label>
    {children}
  </div>
);

export const SnapchatDeliveryPanel = ({ value, onChange, currency }) => {
  const d = { ...defaultSnapDelivery(), ...(value || {}) };
  const set = (patch) => onChange({ ...d, ...patch });
  const togglePos = (p) => set({ positions: d.positions.includes(p) ? d.positions.filter((x) => x !== p) : [...d.positions, p] });

  return (
    <div className="space-y-3" data-testid="snapchat-delivery-panel">
      <p className="text-xs text-yellow-200/80 bg-[#FFFC00]/10 border border-[#FFFC00]/30 rounded-lg p-2">Snapchat delivery settings — placements, bidding, pacing, frequency cap and brand safety (billed per impression).</p>

      <Block icon={LayoutGrid} title="Placements" testId="snapchat-placement-block">
        <div className="flex gap-2 p-1 bg-white/5 rounded-lg">
          {[['AUTOMATIC', 'Automatic (recommended)'], ['CUSTOM', 'Choose placements']].map(([v, label]) => (
            <button key={v} type="button" onClick={() => set({ placement: v })} data-testid={`snapchat-placement-${v}`} className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${d.placement === v ? 'bg-[#FFFC00] text-black' : 'text-white/60 hover:text-white hover:bg-white/10'}`}>{label}</button>
          ))}
        </div>
        {d.placement === 'CUSTOM' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
            {SNAPCHAT_POSITIONS.map((p) => (
              <label key={p.value} className="flex items-center gap-2 p-2 rounded-lg bg-black/20 text-xs text-white/80 cursor-pointer">
                <input type="checkbox" checked={d.positions.includes(p.value)} onChange={() => togglePos(p.value)} data-testid={`snapchat-position-${p.value}`} className="accent-[#FFFC00]" />
                {p.label}
              </label>
            ))}
          </div>
        )}
      </Block>

      <Block icon={Gauge} title="Bidding & pacing" testId="snapchat-bidding-block">
        <Select value={d.bidStrategy} onValueChange={(v) => set({ bidStrategy: v })}>
          <SelectTrigger className={selectCls} data-testid="snapchat-bid-strategy-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-800 border-white/10">
            {SNAPCHAT_BID_STRATEGIES.map((b) => (
              <SelectItem key={b.value} value={b.value} className="text-white hover:bg-white/10">
                <div><div className="font-medium">{b.label}</div><div className="text-xs text-white/50">{b.description}</div></div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {d.bidStrategy !== 'AUTO_BID' && (
          <div className="space-y-1">
            <Label className="text-xs text-white/60">{d.bidStrategy === 'TARGET_COST' ? 'Target cost per result' : 'Maximum bid per result'} ({currency || 'account currency'})</Label>
            <Input type="number" min="0" step="0.01" value={d.bidAmount} onChange={(e) => set({ bidAmount: e.target.value })} placeholder="e.g. 0.50" className={selectCls} data-testid="snapchat-bid-amount-input" />
          </div>
        )}
        <Select value={d.pacing} onValueChange={(v) => set({ pacing: v })}>
          <SelectTrigger className={selectCls} data-testid="snapchat-pacing-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-800 border-white/10">
            {SNAPCHAT_PACING.map((p) => <SelectItem key={p.value} value={p.value} className="text-white hover:bg-white/10">{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </Block>

      <Block icon={Timer} title="Frequency cap" testId="snapchat-frequency-block">
        <label className="flex items-center gap-3 cursor-pointer">
          <Switch checked={d.frequencyCapEnabled} onCheckedChange={(v) => set({ frequencyCapEnabled: v })} data-testid="snapchat-frequency-switch" />
          <span className="text-sm text-white">Limit how often one Snapchatter sees this ad</span>
        </label>
        {d.frequencyCapEnabled && (
          <div className="flex items-center gap-2 text-sm text-white/80">
            <span>Max</span>
            <Input type="number" min="1" max="100" value={d.frequencyCapCount} onChange={(e) => set({ frequencyCapCount: e.target.value })} className={`${selectCls} w-20`} data-testid="snapchat-frequency-count-input" />
            <span>impressions every</span>
            <Input type="number" min="1" max="30" value={d.frequencyCapDays} onChange={(e) => set({ frequencyCapDays: e.target.value })} className={`${selectCls} w-20`} data-testid="snapchat-frequency-days-input" />
            <span>day(s)</span>
          </div>
        )}
      </Block>

      <Block icon={ShieldCheck} title="Brand safety" testId="snapchat-brand-safety-block">
        <Select value={d.brandSafety} onValueChange={(v) => set({ brandSafety: v })}>
          <SelectTrigger className={selectCls} data-testid="snapchat-brand-safety-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-slate-800 border-white/10">
            {SNAPCHAT_BRAND_SAFETY.map((b) => <SelectItem key={b.value} value={b.value} className="text-white hover:bg-white/10">{b.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <p className="text-xs text-white/40">Limited inventory only runs alongside Creator stories and may reduce reach.</p>
      </Block>
    </div>
  );
};
